import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Paragraph } from '@digdir/designsystemet-react';
import { BarChartIcon, PersonGroupIcon, ShieldLockIcon } from '@navikt/aksel-icons';
import type { FactorDimension } from '../../../api/hms';
import type { DimensionSummary } from '../../../api/workEnvironmentTypes';
import { AssessedRisk, keyFigures, summaryText } from '../reportView';
import { ReportSection } from './ReportSection';
import classes from '../WorkEnvironmentReport.module.css';

type Props = {
  risk: AssessedRisk;
  dimensions: Record<FactorDimension, DimensionSummary | null>;
  expected: number;
};

const ICONS = { responses: PersonGroupIcon, high: ShieldLockIcon, wide: BarChartIcon } as const;

export const SummarySection = ({ risk, dimensions, expected }: Props): ReactElement => {
  const { t, i18n } = useTranslation();
  return (
    <ReportSection section="summary" hiddenHeading>
      <Paragraph data-size="lg" className={classes.summaryText}>
        {summaryText(risk, dimensions, t, i18n.language)}
      </Paragraph>
      <dl aria-label={t('workEnvironmentReport.figures.label')} className={classes.figures}>
        {keyFigures(risk, expected, t).map((figure) => {
          const Icon = ICONS[figure.key as keyof typeof ICONS];
          return (
            <div key={figure.key} className={classes.figure} data-color={figure.tone}>
              <span className={classes.figureIcon} aria-hidden="true">
                {Icon && <Icon />}
              </span>
              <div className={classes.figureText}>
                <dt className="ds-paragraph" data-size="xs">
                  {figure.label}
                </dt>
                <dd className={`ds-heading ${classes.figureValue}`} data-size="sm">
                  {figure.value}
                </dd>
                <dd className={`ds-paragraph ${classes.subtle}`} data-size="xs">
                  {figure.sub}
                </dd>
              </div>
            </div>
          );
        })}
      </dl>
    </ReportSection>
  );
};
